import { atom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

/**
 * Open state for the B2B lead modal. Module-level Jotai atoms mirror the HDE
 * registration-modal pattern so any CTA anywhere on the page can open the modal
 * without prop-drilling. The single `<LeadModal>` instance (rendered once by
 * `B2BLandingPage`) reads these atoms.
 */
export const leadModalOpenAtom = atom(false);

/** Label of the CTA that opened the modal — useful for tracking / tailoring copy. */
export const leadModalCtaAtom = atom<string | undefined>(undefined);

export interface OpenLeadModalOptions {
    /** The CTA label that triggered the modal (e.g. "Apply to Join"). */
    ctaLabel?: string;
}

/** Hook exposing imperative open/close controls for the lead modal. */
export function useLeadModal() {
    const setOpen = useSetAtom(leadModalOpenAtom);
    const setCta = useSetAtom(leadModalCtaAtom);

    const openModal = useCallback(
        (options: OpenLeadModalOptions = {}) => {
            setCta(options.ctaLabel);
            setOpen(true);
        },
        [setCta, setOpen],
    );

    const closeModal = useCallback(() => setOpen(false), [setOpen]);

    return { openModal, closeModal };
}
